import { HymnSearchResult, searchHymns } from "@/services/hymnsApi";
import { getHymnSlug, scrollToTop } from "@/utils";
import {
  AlertTriangle,
  ChevronRight,
  Loader2,
  Music,
  Search,
  X,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

interface HymnSearchBarProps {
  placeholder?: string;
  defaultSection?: string;
  className?: string;
  size?: "compact" | "normal";
  id?: string;
}

export const HymnSearchBar: React.FC<HymnSearchBarProps> = ({
  placeholder = "Rechercher par titre ou paroles (ex: Jezi, 12, Lave'm)...",
  defaultSection,
  className = "",
  size = "normal",
  id = "global-hymn-search-input",
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<HymnSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setIsLoading(false);
      setHasError(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsLoading(true);
      setHasError(false);
      try {
        const hits = await searchHymns(query.trim(), defaultSection, 8);
        setResults(hits);
        setIsOpen(true);
      } catch (err) {
        console.error("Search error:", err);
        setHasError(true);
        setIsOpen(true);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, defaultSection]);

  // Handle outside click / touch to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const handleSelectHymn = (hymn: HymnSearchResult) => {
    setIsOpen(false);
    setQuery("");
    scrollToTop();
    const slug = getHymnSlug(hymn.number, hymn.title);
    navigate(
      `/chants-desperance/${encodeURIComponent(hymn.section_code)}/${slug}`,
    );
  };

  const isCompact = size === "compact";

  return (
    <div
      ref={containerRef}
      className={`relative w-full ${isCompact ? "" : "max-w-3xl mx-auto"} ${className}`}
    >
      {/* Search Input Bar */}
      <div className="relative flex items-center">
        <input
          id={id}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            if (results.length > 0) setIsOpen(true);
          }}
          placeholder={placeholder}
          className={`w-full text-white placeholder:text-slate-400 font-medium caret-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-400/50 focus:border-sky-400 backdrop-blur-md transition-all selection:bg-sky-500/30 selection:text-white bg-slate-900/95 border border-slate-700/80 ${
            isCompact
              ? "pl-10 pr-9 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm shadow-md hover:border-slate-600"
              : "pl-12 sm:pl-14 pr-10 sm:pr-12 py-3.5 sm:py-4 rounded-2xl text-sm sm:text-base shadow-2xl"
          }`}
        />

        <div
          className={`absolute z-10 pointer-events-none flex items-center justify-center text-sky-400 ${
            isCompact ? "left-3" : "left-4"
          }`}
        >
          {isLoading ? (
            <Loader2
              className={`animate-spin text-sky-400 ${
                isCompact ? "w-4 h-4" : "w-5 h-5 sm:w-6 sm:h-6"
              }`}
            />
          ) : (
            <Search
              className={`text-sky-400 ${
                isCompact ? "w-4 h-4" : "w-5 h-5 sm:w-6 sm:h-6"
              }`}
            />
          )}
        </div>

        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setResults([]);
              setIsOpen(false);
            }}
            className={`absolute z-10 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors cursor-pointer ${
              isCompact
                ? "right-2.5 p-1"
                : "right-3 sm:right-4 p-1.5 rounded-xl"
            }`}
          >
            <X
              className={isCompact ? "w-3.5 h-3.5" : "w-4 h-4 sm:w-5 sm:h-5"}
            />
          </button>
        )}
      </div>

      {/* Floating Dropdown Results with High-Contrast */}
      {isOpen && results.length > 0 && (
        <div
          className={`absolute top-full left-0 right-0 ${
            isCompact ? "sm:right-auto sm:w-[420px]" : ""
          } mt-2 rounded-2xl bg-slate-950/98 border border-slate-700/90 shadow-[0_20px_50px_rgba(0,0,0,0.9)] ring-1 ring-white/10 backdrop-blur-2xl overflow-hidden z-50 divide-y divide-slate-800/90`}
        >
          <div className="px-3.5 py-2 sm:px-4 sm:py-2.5 bg-slate-900/90 text-xs font-bold text-slate-300 flex items-center justify-between border-b border-slate-800/90">
            <span>{results.length} cantiques trouvés</span>
            <span className="text-[11px] text-sky-400 font-semibold">
              Appuyez pour ouvrir
            </span>
          </div>

          <div className="max-h-72 sm:max-h-96 overflow-y-auto divide-y divide-slate-850">
            {results.map((hymn, idx) => (
              <div
                key={hymn.id || idx}
                onClick={() => handleSelectHymn(hymn)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSelectHymn(hymn);
                }}
                className="flex items-center justify-between p-3 sm:p-3.5 bg-slate-900/100 hover:bg-slate-900/90 active:bg-slate-800/90 transition-colors cursor-pointer text-left group"
              >
                <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 pr-2">
                  <span className="px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-lg bg-sky-950 border border-sky-400/50 text-sky-300 font-serif font-black text-[11px] sm:text-xs flex-shrink-0 shadow-sm shadow-sky-950">
                    {hymn.section_code} N° {hymn.number}
                  </span>

                  <div className="min-w-0">
                    <p className="font-bold text-white text-xs sm:text-sm truncate group-hover:text-sky-300 transition-colors">
                      {hymn.title}
                    </p>
                    {hymn.snippet ? (
                      <p
                        className="text-[11px] sm:text-xs text-slate-300 truncate mt-0.5 [&_mark]:bg-amber-400/40 [&_mark]:text-amber-200 [&_mark]:font-bold [&_em]:text-amber-300 [&_em]:font-bold [&_b]:text-white"
                        dangerouslySetInnerHTML={{ __html: hymn.snippet }}
                      />
                    ) : (
                      <p className="text-[11px] sm:text-xs text-slate-400 truncate mt-0.5">
                        {hymn.section}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                  {hymn.media_url && (
                    <span className="p-1 sm:p-1.5 rounded-lg bg-emerald-950x borderx border-emerald-500/40x text-emerald-400 shadow-sm shadow-emerald-950">
                      <Music className="w-3.5 h-3.5" />
                    </span>
                  )}
                  <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-sky-300 group-hover:translate-x-0.5 transition-all" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isOpen && query.trim() && !isLoading && hasError && (
        <div
          className={`absolute top-full left-0 right-0 ${
            isCompact ? "sm:right-auto sm:w-[380px]" : ""
          } mt-2 p-4 sm:p-6 rounded-2xl bg-slate-950/98 border border-rose-500/40 shadow-[0_20px_50px_rgba(0,0,0,0.9)] ring-1 ring-white/10 backdrop-blur-2xl text-center z-50`}
        >
          <div className="flex items-center justify-center gap-2 text-rose-400 mb-1 font-semibold text-xs sm:text-sm">
            <AlertTriangle className="w-4 h-4" />
            <span>Erreur de recherche</span>
          </div>
          <p className="text-slate-300 text-xs sm:text-sm">
            Impossible de contacter le serveur de recherche. Veuillez vérifier
            votre connexion.
          </p>
        </div>
      )}

      {isOpen &&
        query.trim() &&
        !isLoading &&
        !hasError &&
        results.length === 0 && (
          <div
            className={`absolute top-full left-0 right-0 ${
              isCompact ? "sm:right-auto sm:w-[380px]" : ""
            } mt-2 p-4 sm:p-5 rounded-2xl bg-slate-950/98 border border-slate-700/90 shadow-[0_20px_50px_rgba(0,0,0,0.9)] ring-1 ring-white/10 backdrop-blur-2xl text-center z-50`}
          >
            <p className="text-slate-300 text-xs sm:text-sm">
              Aucun chant trouvé pour "{query}". Essayez un autre mot-clé ou
              numéro.
            </p>
          </div>
        )}
    </div>
  );
};
